import type { ElementRef, OnChanges, SimpleChanges } from '@angular/core';

import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, Output, ViewChild } from '@angular/core';
import { IWorktask, WorktaskService } from '@shared/services/worktask.service';

type WorktaskSelectValue = IWorktask | string | null;
type WorktaskSelectValueMode = 'id' | 'object';

@Component({
	selector: 'app-worktask-select',
	templateUrl: './worktask-select.component.html',
	styleUrl: './worktask-select.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class WorktaskSelectComponent implements OnChanges {
	@HostBinding('class.full-width') get fullWidthClass(): boolean {
		return this.fullWidth;
	}

	@Input() worktasks: IWorktask[] = [];
	@Input() value: WorktaskSelectValue = '';
	@Input() label = '';
	@Input() placeholder = '';
	@Input() filterPlaceholder = '';
	@Input() includeNoneOption = false;
	@Input() noneText = '';
	@Input() fullWidth = false;
	@Input() disabled = false;
	@Input() excludeOfftask = false;
	@Input() valueMode: WorktaskSelectValueMode = 'id';
	@Output() valueChange = new EventEmitter<WorktaskSelectValue>();
	@ViewChild('workTaskFilterInput')
	workTaskFilterInput: ElementRef<HTMLInputElement>;

	filter = '';
	filteredWorkTasks: IWorktask[] = [];

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['worktasks'] || changes['excludeOfftask']) {
			this.resetFilteredWorkTasks();
		}
	}

	onOpenedChange(opened: boolean): void {
		if (opened) {
			setTimeout(() => this.workTaskFilterInput?.nativeElement?.focus());
		} else {
			this.filter = '';
			this.resetFilteredWorkTasks();
		}
	}

	onSelectionChange(value: WorktaskSelectValue): void {
		this.valueChange.emit(value);
	}

	filterWorkTasks(): void {
		const filter = this.filter.toLowerCase();
		this.filteredWorkTasks = this.selectableWorkTasks.filter((wt) => wt.alias.toLowerCase().includes(filter) || wt.name.toLowerCase().includes(filter));
	}

	getOptionValue(workTask: IWorktask): WorktaskSelectValue {
		return this.valueMode === 'object' ? workTask : workTask.id;
	}

	trackByWorkTaskId(_index: number, workTask: IWorktask): string {
		return workTask.id;
	}

	private resetFilteredWorkTasks(): void {
		this.filteredWorkTasks = [...this.selectableWorkTasks];
	}

	private get selectableWorkTasks(): IWorktask[] {
		if (!this.excludeOfftask) {
			return this.worktasks ?? [];
		}
		return (this.worktasks ?? []).filter((wt) => wt.id !== WorktaskService.offWorkTaskId);
	}
}
